import { useEffect, useMemo, useState } from "react";
import { CaretLeft, CaretRight, FilePdf } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import KpiCards from "@/components/metrics/KpiCards";
import FlowChart from "@/components/metrics/FlowChart";
import BalanceChart from "@/components/metrics/BalanceChart";
import CoverageChart from "@/components/metrics/CoverageChart";
import ExpenseMixRanking from "@/components/metrics/ExpenseMixRanking";
import MonthlyTable from "@/components/metrics/MonthlyTable";
import SalesChart from "@/components/metrics/SalesChart";
import { monthlyRows, yearTotals, money, pct } from "@/components/metrics/metricsMath";

const Sec = ({ title, children }) => (
  <section className="flex flex-col gap-2">
    <h2 className="font-heading font-bold text-base md:text-lg m-0">{title}</h2>
    {children}
  </section>
);

// Métricos del año: se renueva solo cada año (por defecto el año en curso; flechas para ver años anteriores).
export default function Metrics() {
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(thisYear);
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // PDF generado en el servidor con todas las cifras del año (tablas + gráficas).
  const downloadPdf = async () => {
    setDownloading(true);
    toast.loading("Generando PDF…", { id: "metrics-pdf" });
    try {
      const res = await api.get("/metrics/yearly/pdf", { params: { year }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `metricos_financieros_${year}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 30000);
      toast.success("PDF descargado", { id: "metrics-pdf" });
    } catch { toast.error("No se pudo generar el PDF", { id: "metrics-pdf" }); }
    finally { setDownloading(false); }
  };

  useEffect(() => {
    let alive = true;
    api.get("/metrics/yearly", { params: { year } })
      .then((r) => { if (alive) { setData(r.data); setError(false); } })
      .catch(() => { if (alive) { setData(null); setError(true); } });
    return () => { alive = false; };
  }, [year]);

  const rows = useMemo(() => monthlyRows(data?.months), [data]);
  const t = useMemo(() => yearTotals(rows), [rows]);
  const years = data?.years?.length ? data.years : [year];
  const canPrev = year > Math.min(years[0], thisYear - 1), canNext = year < thisYear;  // siempre se puede ver el año anterior
  const enCurso = year === thisYear;

  return (
    <div className="max-w-[1280px] mx-auto flex flex-col gap-8" data-testid="metrics-page">
      <header className="flex items-start justify-between flex-wrap gap-4">
        <div className="max-w-[60rem]">
          <h1 className="font-heading text-3xl font-extrabold tracking-tight m-0" data-testid="metrics-title">Métricos financieros {year}</h1>
          {t ? (
            <p className="text-sm md:text-[15px] text-zinc-500 mt-1 leading-relaxed m-0" data-testid="metrics-summary">
              {enCurso ? "En lo que va del año" : `En ${year}`} entraron <b className="text-zinc-900 numeric">{money(t.entro)}</b> y salieron <b className="text-zinc-900 numeric">{money(t.salio)}</b>. El saldo cerró {t.mesLargo} en <b className="text-zinc-900 numeric">{money(t.saldo)}</b>
              {t.pctRegistrado != null && <>, {t.pctRegistrado >= 0.95 ? "y" : "pero solo"} <b className="text-zinc-900 numeric">{pct(t.pctRegistrado)}</b> de lo que sale está registrado como gasto</>}.
            </p>
          ) : (
            <p className="text-sm text-zinc-500 mt-1 m-0">Gastos vs dinero real en movimiento: entradas y salidas de bancos mes a mes.</p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button type="button" onClick={downloadPdf} disabled={downloading || !data} className="h-9 px-3 rounded-md bg-[#002FA7] hover:bg-[#001a6a] text-white text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="metrics-pdf">
            <FilePdf size={15} weight="bold" /> {downloading ? "Generando…" : "Descargar PDF"}
          </button>
          <div className="inline-flex items-center border border-zinc-200 bg-white rounded-md" data-testid="metrics-year-nav">
            <button type="button" disabled={!canPrev} onClick={() => setYear((y) => y - 1)} className="px-2.5 py-2 hover:bg-zinc-50 disabled:opacity-30 rounded-l-md" data-testid="metrics-prev-year"><CaretLeft size={14} weight="bold" /></button>
            <span className="font-mono-data font-bold text-sm px-3 tabular-nums" data-testid="metrics-year">{year}</span>
            <button type="button" disabled={!canNext} onClick={() => setYear((y) => y + 1)} className="px-2.5 py-2 hover:bg-zinc-50 disabled:opacity-30 rounded-r-md" data-testid="metrics-next-year"><CaretRight size={14} weight="bold" /></button>
          </div>
        </div>
      </header>

      {error ? (
        <div className="border border-zinc-200 bg-white rounded-lg p-10 text-center text-sm text-[#E0000F]" data-testid="metrics-error">No se pudieron cargar los métricos. Intenta de nuevo.</div>
      ) : !data ? (
        <div className="border border-zinc-200 bg-white rounded-lg p-10 text-center text-sm text-zinc-400">Cargando…</div>
      ) : !t ? (
        <div className="border border-zinc-200 bg-white rounded-lg p-10 text-center text-sm text-zinc-400 dotted-bg" data-testid="metrics-empty">Sin movimientos ni gastos en {year}.</div>
      ) : (
        <>
          <KpiCards rows={rows} t={t} year={year} />

          <Sec title="¿Cómo se movió el dinero?">
            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] gap-4">
              <FlowChart rows={rows} />
              <BalanceChart rows={rows} fx={data.fx} />
            </div>
          </Sec>

          <Sec title="¿A dónde se va lo que sale?">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <CoverageChart rows={rows} sinTotal={t.sin} />
              <ExpenseMixRanking key={year} mix={data.expense_mix} rows={rows} />
            </div>
          </Sec>

          <Sec title="¿Cuánto se vendió?">
            <SalesChart rows={rows} total={t.ventas} count={t.notas} year={year} />
          </Sec>

          <Sec title="Detalle mes a mes">
            <MonthlyTable rows={rows} year={year} />
          </Sec>
        </>
      )}
    </div>
  );
}
